import React, { Fragment } from 'react';
import {
  useFetcher,
  useLoaderData,
  useNavigate,
  useNavigation,
  useOutlet,
  useOutletContext,
  useRevalidator,
  useSubmit
} from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import { PencilSquareIcon, PhoneArrowUpRightIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { Await } from '@oysterjs/ui/async';
import { Button } from '@oysterjs/ui/button';
import { Listbox } from '@oysterjs/ui/listbox';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import { Spinner } from '@oysterjs/ui/spinner';
import {
  CancelOutboundCallMutation,
  DialOutboundCallMutation,
  OutboundCallAttemptFieldsFragment,
  OutboundCallDetailFieldsFragment,
  Action,
  Resource,
  useGetOutboundCallFrequencyQuery
} from '~/api/graphql';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { usePermissions } from '~/components/permissions';

const UPDATE_OUTBOUND_CALL = [{ resource: Resource.OutboundCall, action: Action.Update }];
// Marking an attempt seen goes through updateCalls, so it is a CALL update.
const UPDATE_CALL = [{ resource: Resource.Call, action: Action.Update }];
import { ArchiveConfirmDialog } from '~/components/list/archive-confirm-dialog';
import {
  ConnectedCallConversation,
  hasConnectedCallConversation,
  type ConnectedCall
} from '~/components/calls/call-detail-sections';
import { MarkCallSeenButton } from '~/components/calls/call-seen';
import {
  CallStatusBadge,
  dateFormatter,
  formatDuration,
  getCallDurationSeconds,
  titleCase
} from '~/components/calls/calls-columns';
import { OutboundCallStatusBadge } from '~/components/calls/outbound-calls-columns';
import {
  formatOutboundCallCreatorLabel,
  formatOutboundCallFrequencyDescription,
  formatOutboundCallScheduleFromApi,
  getOutboundCallActionErrorMessage,
  isOutboundCallActive,
  isOutboundCallCancellable,
  isOutboundCallDetailLive,
  isOutboundCallDialable,
  isOutboundCallEditable,
  OUTBOUND_CALLS_LIVE_POLL_MS,
  truncateOutboundText
} from '~/components/calls/outbound-call';
import type { OutboundCallsOutletContext } from './outbound-calls';

const pickDefaultAttemptId = (
  calls: OutboundCallAttemptFieldsFragment[],
  latestCall?: OutboundCallAttemptFieldsFragment | null
): string | null => latestCall?.id ?? calls.at(-1)?.id ?? null;

const formatAttemptSubtitle = (attempt: OutboundCallAttemptFieldsFragment): string => {
  const started = dateFormatter.format(new Date(attempt.startedAt));
  if (!attempt.endedAt) {
    return started;
  }
  return `${started} · ${formatDuration(getCallDurationSeconds(attempt))}`;
};

const OutboundCallAttemptListboxOption = (props: {
  value: string;
  attempt: OutboundCallAttemptFieldsFragment;
  attemptNumber: number;
}) => {
  const content = (
    <>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-neutral-900 dark:text-white group-data-[focus]/option:text-white">
          Attempt {props.attemptNumber}
        </div>
        <div className="text-xs text-neutral-500 dark:text-neutral-400 group-data-[focus]/option:text-white/80">
          {formatAttemptSubtitle(props.attempt)}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1 py-1">
        <CallStatusBadge
          status={props.attempt.status}
          className="group-data-[focus]/option:bg-white/20 group-data-[focus]/option:text-white"
        />
        {props.attempt.disconnectionReason && (
          <span className="text-xs text-neutral-500 dark:text-neutral-400 group-data-[focus]/option:text-white/80">
            {titleCase(props.attempt.disconnectionReason)}
          </span>
        )}
      </div>
    </>
  );

  return (
    <Headless.ListboxOption as={Fragment} value={props.value}>
      {({ selectedOption }) => {
        if (selectedOption) {
          return <div className="flex w-full items-center justify-between gap-3">{content}</div>;
        }

        return (
          <div
            className={clsx(
              'group/option grid cursor-default grid-cols-[theme(spacing.5),1fr] items-center gap-x-2 rounded-lg py-2.5 pl-2 pr-3.5 sm:grid-cols-[theme(spacing.4),1fr] sm:py-1.5 sm:pl-1.5 sm:pr-3',
              'text-base/6 text-neutral-950 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]',
              'outline-none data-[focus]:bg-primary-500 data-[focus]:text-white',
              'forced-color-adjust-none forced-colors:data-[focus]:bg-[Highlight] forced-colors:data-[focus]:text-[HighlightText]',
              'data-[disabled]:opacity-50'
            )}
          >
            <svg
              className="relative hidden size-5 self-center stroke-current group-data-[selected]/option:inline sm:size-4"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 8.5l3 3L12 4"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="col-start-2 flex w-full items-center justify-between gap-3">
              {content}
            </div>
          </div>
        );
      }}
    </Headless.ListboxOption>
  );
};

const DetailField = (props: { label: string; value: React.ReactNode; stacked?: boolean }) =>
  props.stacked ? (
    <div className="flex flex-col gap-1">
      <div className="text-sm/6 font-medium text-neutral-900 dark:text-white">{props.label}</div>
      <div className="text-sm/6 text-neutral-700 dark:text-neutral-300">{props.value}</div>
    </div>
  ) : (
    <div className="sm:grid sm:grid-cols-3 sm:gap-4">
      <div className="text-sm/6 font-medium text-neutral-900 dark:text-white">{props.label}</div>
      <div className="text-sm/6 text-neutral-700 dark:text-neutral-300 sm:col-span-2">
        {props.value}
      </div>
    </div>
  );

const OutboundCallDetailContent = (props: { outboundCall: OutboundCallDetailFieldsFragment }) => {
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const fetcher = useFetcher<ActionResult<DialOutboundCallMutation>>();
  const cancelFetcher = useFetcher<ActionResult<CancelOutboundCallMutation>>();
  const { outboundCall } = props;
  const attempts = outboundCall.calls ?? [];
  const frequencyCapEnabled = useFlag(FEATURE_FLAGS.OUTBOUND_CALL_FREQUENCY_CAP);

  const { data: frequencyData } = useGetOutboundCallFrequencyQuery({
    variables: { phone: outboundCall.customer.phone },
    skip: !frequencyCapEnabled,
    fetchPolicy: 'cache-and-network'
  });
  const frequency = frequencyData?.outboundCallFrequency;

  const [selectedAttemptId, setSelectedAttemptId] = React.useState<string | null>(() =>
    pickDefaultAttemptId(attempts, outboundCall.latestCall)
  );

  React.useEffect(() => {
    setSelectedAttemptId(pickDefaultAttemptId(attempts, outboundCall.latestCall));
  }, [outboundCall.id, outboundCall.latestCall?.id, attempts.length]);

  React.useEffect(() => {
    if (!isOutboundCallDetailLive(outboundCall.status)) {
      return;
    }
    const intervalId = window.setInterval(() => {
      revalidator.revalidate();
    }, OUTBOUND_CALLS_LIVE_POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [outboundCall.status, revalidator]);

  // Editing, cancelling and dialling are all outbound-call updates.
  const canUpdate = usePermissions().hasAllPermissions(UPDATE_OUTBOUND_CALL);

  const [dialError, setDialError] = React.useState<string | null>(null);
  const [cancelError, setCancelError] = React.useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
  const isDialing = fetcher.state !== 'idle';
  const isCancelling = cancelFetcher.state !== 'idle';

  React.useEffect(() => {
    if (fetcher.state !== 'idle' || !fetcher.data) {
      return;
    }
    const message = getOutboundCallActionErrorMessage(fetcher.data);
    if (message) {
      setDialError(message);
      return;
    }
    setDialError(null);
    if (fetcher.data.data?.dialOutboundCall) {
      revalidator.revalidate();
    }
  }, [fetcher.state, fetcher.data, revalidator]);

  React.useEffect(() => {
    if (cancelFetcher.state !== 'idle' || !cancelFetcher.data) {
      return;
    }
    const message = getOutboundCallActionErrorMessage(cancelFetcher.data);
    if (message) {
      setCancelError(message);
      return;
    }
    setCancelError(null);
    setCancelDialogOpen(false);
    if (cancelFetcher.data.data?.cancelOutboundCall) {
      revalidator.revalidate();
    }
  }, [cancelFetcher.state, cancelFetcher.data, revalidator]);

  const selectedAttempt = attempts.find((call) => call.id === selectedAttemptId) ?? null;
  const showConversation =
    selectedAttempt != null && hasConnectedCallConversation(selectedAttempt as ConnectedCall);

  const dialNow = () => {
    setDialError(null);
    fetcher.submit(
      {},
      {
        method: 'post',
        action: `/app/calls/outbound/${outboundCall.id}/dial`,
        encType: 'application/json'
      }
    );
  };

  const confirmCancel = () => {
    setCancelError(null);
    cancelFetcher.submit(
      {},
      {
        method: 'post',
        action: `/app/calls/outbound/${outboundCall.id}/cancel`,
        encType: 'application/json'
      }
    );
  };

  return (
    <>
      <div className="mb-6 flex flex-wrap gap-2">
        {canUpdate && isOutboundCallEditable(outboundCall.status) && (
          <Button type="button" color="white" onClick={() => navigate('edit')}>
            <PencilSquareIcon className="size-4" />
            Edit call
          </Button>
        )}
        {canUpdate && isOutboundCallCancellable(outboundCall.status) && (
          <Button type="button" color="white" onClick={() => setCancelDialogOpen(true)}>
            <NoSymbolIcon className="size-4" />
            Cancel call
          </Button>
        )}
        {canUpdate && isOutboundCallDialable(outboundCall.status) && (
          <Button type="button" color="sky" onClick={dialNow} loading={isDialing}>
            <PhoneArrowUpRightIcon className="size-4" />
            Call now
          </Button>
        )}
      </div>
      {cancelError && (
        <div className="mb-4 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-900 dark:text-red-200">
          {cancelError}
        </div>
      )}
      {dialError && (
        <div className="mb-4 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-900 dark:text-red-200">
          {dialError}
        </div>
      )}

      <ArchiveConfirmDialog
        open={cancelDialogOpen}
        count={1}
        itemNoun="call"
        title="Cancel call"
        dismissLabel="Back"
        confirmLabel="Cancel call"
        loading={isCancelling}
        description={<>Are you sure you want to cancel this call? It will not be dialed.</>}
        onCancel={() => {
          setCancelDialogOpen(false);
          setCancelError(null);
        }}
        onConfirm={confirmCancel}
      />

      <dl>
        <div className="flex flex-col sm:gap-1.5 gap-3 pb-6">
          <DetailField label="Customer" value={outboundCall.customer.name || 'Unnamed customer'} />
          <DetailField label="Phone" value={outboundCall.customer.phone} />
          {frequencyCapEnabled && frequency && (
            <DetailField
              label="Frequency"
              value={
                <span
                  className={
                    frequency.remaining === 0 ? 'text-amber-700 dark:text-amber-300' : undefined
                  }
                >
                  {formatOutboundCallFrequencyDescription(
                    frequency.count,
                    frequency.limit,
                    frequency.windowDays
                  )}
                </span>
              }
            />
          )}
          <DetailField
            label="Status"
            value={<OutboundCallStatusBadge status={outboundCall.status} />}
          />
          <DetailField
            label="Scheduled"
            value={
              isOutboundCallActive(outboundCall.status)
                ? 'Calling now'
                : formatOutboundCallScheduleFromApi(outboundCall)
            }
          />
          <DetailField
            label="Created by"
            value={formatOutboundCallCreatorLabel(outboundCall.createdBy)}
          />
        </div>

        <div className="flex flex-col sm:gap-1.5 gap-3 pb-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
          <div className="flex flex-col gap-3">
            <DetailField
              label="Objective"
              stacked
              value={truncateOutboundText(outboundCall.callConfig.callObjective, 300)}
            />
            {outboundCall.callConfig.customerContext?.trim() && (
              <DetailField
                label="Customer context"
                stacked
                value={truncateOutboundText(outboundCall.callConfig.customerContext, 200)}
              />
            )}
            {outboundCall.callConfig.guidelines?.trim() && (
              <DetailField
                label="Guidelines"
                value={truncateOutboundText(outboundCall.callConfig.guidelines, 200)}
              />
            )}
          </div>
        </div>

        <div className="flex flex-col sm:gap-1.5 gap-3 pt-6 border-t border-neutral-200 dark:border-neutral-700">
          {attempts.length === 0 ? (
            <div className="text-sm/6 text-neutral-500 dark:text-neutral-400">
              No dial attempts yet.
            </div>
          ) : (
            <Listbox
              value={selectedAttemptId}
              onChange={setSelectedAttemptId}
              aria-label="Call attempt"
              className="w-full"
            >
              {attempts.map((attempt, index) => (
                <OutboundCallAttemptListboxOption
                  key={attempt.id}
                  value={attempt.id}
                  attempt={attempt}
                  attemptNumber={index + 1}
                />
              ))}
            </Listbox>
          )}
        </div>

        {showConversation && selectedAttempt && (
          <div className="py-6">
            <ConnectedCallConversation call={selectedAttempt} />
          </div>
        )}
      </dl>
    </>
  );
};

export const OutboundCallDetailPage = () => {
  const navigate = useNavigate();
  const submit = useSubmit();
  const navigation = useNavigation();
  const canMarkSeen = usePermissions().hasAllPermissions(UPDATE_CALL);
  const { agents } = useOutletContext<OutboundCallsOutletContext>();
  const outlet = useOutlet({ agents });
  const loader = useLoaderData() as
    | {
        data: Promise<{ outboundCall: OutboundCallDetailFieldsFragment }>;
      }
    | undefined;

  const onClose = () => {
    navigate('/app/calls/outbound', { replace: true });
  };

  const toggleSeen = (callId: string, markSeen: boolean) => {
    submit(
      { callIds: [callId], markSeen },
      { method: 'POST', action: '/app/calls/outbound/mark-seen', encType: 'application/json' }
    );
  };

  const isTogglingSeenState =
    navigation.state === 'submitting' && navigation.formAction?.endsWith('/mark-seen');

  // One MountableSlideout for the whole page — nesting Suspense fallbacks that each
  // mount their own slideout leaves an empty, unclosable panel after AnimatePresence exit.
  return (
    <MountableSlideout
      onClose={onClose}
      title="Outbound Call"
      actions={
        <React.Suspense fallback={null}>
          <Await resolve={loader?.data}>
            {(data) => {
              const latestCall = data?.outboundCall?.latestCall;
              if (!latestCall) {
                return null;
              }
              return (
                canMarkSeen && (
                  <MarkCallSeenButton
                    seenAt={latestCall.seenAt}
                    disabled={isTogglingSeenState}
                    onToggle={(markSeen) => toggleSeen(latestCall.id, markSeen)}
                  />
                )
              );
            }}
          </Await>
        </React.Suspense>
      }
    >
      <React.Suspense
        fallback={
          <div className="flex justify-center py-12">
            <Spinner size="size-8" color="neutral" />
          </div>
        }
      >
        <Await resolve={loader?.data}>
          {(data) =>
            data?.outboundCall ? (
              <OutboundCallDetailContent outboundCall={data.outboundCall} />
            ) : (
              <div className="py-8 text-center text-sm text-neutral-500 dark:text-neutral-400">
                Call not found.
              </div>
            )
          }
        </Await>
      </React.Suspense>
      <AnimatePresence mode="wait">{outlet && React.cloneElement(outlet, {})}</AnimatePresence>
    </MountableSlideout>
  );
};
